import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  downloadDailyMissionAnalyticsCsv,
  getDailyMissionAnalytics,
  getDailyMissionsPayload,
  listDailyMissionAnalyticsParticipants,
} from '../../services/dailyMissionsApi';
import { formatNumber } from '../Missions/missionUtils';
import { addDays, formatMissionDate } from './dailyMissionUtils';
import { useDailyMissionsSession } from './useDailyMissionsSession';

const PAGE_SIZE = 25;

function istToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function participantLabel(row) {
  const name = String(row?.display_name || '').trim();
  if (name) return name;
  const id = String(row?.subject_id || '');
  if (row?.subject_type === 'guest' && id.length > 8) return `${id.slice(0, 6)}..`;
  return id || '—';
}

function formatPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0%';
  return Number.isInteger(number) ? `${number}%` : `${number}%`;
}

function rowKey(row) {
  return `${row.subject_type}:${row.subject_id}`;
}

function SummaryCard({ label, value, detail, selected, onClick }) {
  return (
    <Paper
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 2,
        minWidth: 140,
        flex: '1 1 140px',
        cursor: 'pointer',
        border: '2px solid',
        borderColor: selected ? '#4F46E5' : 'transparent',
        bgcolor: selected ? '#EEF2FF' : '#fafafa',
      }}
    >
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    </Paper>
  );
}

function DailyMissionAnalytics() {
  const [date, setDate] = useState(istToday);
  const [serverToday, setServerToday] = useState(istToday);
  const [subjectType, setSubjectType] = useState('all');
  const [tasksDone, setTasksDone] = useState('any');
  const [taskKey, setTaskKey] = useState('');
  const [offset, setOffset] = useState(0);
  const [summary, setSummary] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [total, setTotal] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const { handleApiResponse } = useDailyMissionsSession(setError);

  const filters = useMemo(
    () => ({
      date,
      subjectType,
      tasksDone,
      taskKey,
    }),
    [date, subjectType, tasksDone, taskKey]
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setError('');
    try {
      const result = await getDailyMissionAnalytics({ date, subjectType });
      if (handleApiResponse(result, 'Failed to load daily mission analytics.')) {
        setSummary(null);
        return;
      }
      const payload = getDailyMissionsPayload(result);
      setSummary(payload);
      if (payload?.today) setServerToday(payload.today);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load daily mission analytics.');
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [date, subjectType, handleApiResponse]);

  const loadParticipants = useCallback(async () => {
    setTableLoading(true);
    setError('');
    try {
      const result = await listDailyMissionAnalyticsParticipants({
        ...filters,
        limit: PAGE_SIZE,
        offset,
      });
      if (handleApiResponse(result, 'Failed to load participants.')) {
        setParticipants([]);
        setTotal(0);
        return;
      }
      const payload = getDailyMissionsPayload(result);
      setParticipants(payload?.participants || []);
      setTotal(Number(payload?.pagination?.total ?? 0));
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load participants.');
      setParticipants([]);
      setTotal(0);
    } finally {
      setTableLoading(false);
    }
  }, [filters, offset, handleApiResponse]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  useEffect(() => {
    if (!taskKey) return;
    const known = (summary?.tasks || []).some((task) => task.task_key === taskKey);
    if (summary && !known) setTaskKey('');
  }, [summary, taskKey]);

  const distribution = useMemo(() => {
    const buckets = summary?.buckets || [];
    const partials = buckets.filter((bucket) => bucket.key !== 'complete').slice().reverse();
    const complete = buckets.find((bucket) => bucket.key === 'complete');
    return [...partials, ...(complete ? [{ ...complete, label: 'All' }] : [])];
  }, [summary]);

  const rateSeries = summary?.completion_rate_30d || [];
  const split = (card) => `User ${formatNumber(card?.user)} · Guest ${formatNumber(card?.guest)}`;

  const chooseTasksDone = (next) => {
    setTasksDone(next);
    setOffset(0);
    setExpandAll(false);
    setExpanded({});
  };

  const exportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      await downloadDailyMissionAnalyticsCsv(filters);
    } catch (exportError) {
      setError(exportError.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + participants.length, total);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2 }}>
        <Typography variant="h6">Daily Mission Analytics</Typography>
        <Button component={Link} to="/admin/daily-missions?from=today" size="small">
          Back to days
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Opens on today (IST). The date filter changes the cards, the charts, and the table. Day resets at midnight IST.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          value={date}
          onChange={(event) => {
            if (!event.target.value) return;
            setDate(event.target.value);
            setOffset(0);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <IconButton aria-label="Previous day" onClick={() => { setDate(addDays(date, -1)); setOffset(0); }}>
          <ChevronLeftIcon />
        </IconButton>
        <IconButton aria-label="Next day" onClick={() => { setDate(addDays(date, 1)); setOffset(0); }}>
          <ChevronRightIcon />
        </IconButton>
        <Button variant="outlined" size="small" onClick={() => { setDate(serverToday); setOffset(0); }} disabled={date === serverToday}>
          Today
        </Button>
        <TextField
          select
          size="small"
          label="User type"
          value={subjectType}
          onChange={(event) => {
            setSubjectType(event.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="user">User</MenuItem>
          <MenuItem value="guest">Guest</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={exportCsv}
          disabled={exporting}
        >
          Export CSV
        </Button>
        <Typography variant="body2" color="text.secondary">
          {formatMissionDate(date)}
          {summary?.mission ? ` · ${summary.mission.title}` : ' · No mission scheduled'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {summaryLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
            <SummaryCard
              label="Participants"
              value={formatNumber(summary?.participants?.total)}
              detail={split(summary?.participants)}
              selected={tasksDone === 'any'}
              onClick={() => chooseTasksDone('any')}
            />
            {(summary?.buckets || []).map((bucket) => (
              <SummaryCard
                key={bucket.key}
                label={bucket.label}
                value={
                  bucket.key === 'complete'
                    ? `${formatNumber(bucket.total)} (${formatPercent(bucket.percent)})`
                    : formatNumber(bucket.total)
                }
                detail={split(bucket)}
                selected={tasksDone === bucket.key}
                onClick={() => chooseTasksDone(bucket.key)}
              />
            ))}
          </Box>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Completion distribution
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Stacked User / Guest. All is fans who finished every task on their copy.
            </Typography>
            {distribution.length === 0 ? (
              <Typography color="text.secondary">No activity on this date.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(220, distribution.length * 48)}>
                <BarChart data={distribution} layout="vertical" margin={{ left: 16, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={80} />
                  <Tooltip />
                  <Bar dataKey="user" name="User" stackId="fans" fill="#4F46E5" />
                  <Bar dataKey="guest" name="Guest" stackId="fans" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Completion rate, last 30 days
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Share of fans who finished every task. The selected date is marked. A gap is a day nobody opened.
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={rateSeries} margin={{ left: 0, right: 16, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(value) => String(value).slice(8)} minTickGap={16} />
                <YAxis unit="%" domain={[0, 100]} allowDecimals={false} />
                <Tooltip
                  formatter={(value) => [value == null ? 'No participants' : `${value}%`, 'Completion rate']}
                  labelFormatter={(value) => formatMissionDate(value)}
                />
                <ReferenceLine x={date} stroke="#111827" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="rate" name="Completion rate" stroke="#4F46E5" connectNulls={false} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 1 }}>
          Participants
        </Typography>
        <TextField
          select
          size="small"
          label="Tasks done"
          value={tasksDone}
          onChange={(event) => chooseTasksDone(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="any">All</MenuItem>
          {(summary?.buckets || []).map((bucket) => (
            <MenuItem key={bucket.key} value={bucket.key}>
              {bucket.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Completed task"
          value={taskKey}
          onChange={(event) => {
            setTaskKey(event.target.value);
            setOffset(0);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Any</MenuItem>
          {(summary?.tasks || []).map((task) => (
            <MenuItem key={task.task_key} value={task.task_key}>
              {task.title || task.task_key}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          onClick={() => {
            setExpandAll((open) => !open);
            setExpanded({});
          }}
        >
          {expandAll ? 'Collapse all' : 'Expand all'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Click a row to see which tasks that fan completed. Day # is how many daily-mission days they have opened through this date.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={48} />
            <TableCell>Participant</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Day #</TableCell>
            <TableCell align="right">Progress</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableLoading ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <CircularProgress size={22} />
              </TableCell>
            </TableRow>
          ) : participants.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>No fans match these filters.</TableCell>
            </TableRow>
          ) : (
            participants.map((row) => {
              const key = rowKey(row);
              const open = expandAll ? expanded[key] !== false : Boolean(expanded[key]);
              return (
                <Fragment key={key}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpanded((current) => ({ ...current, [key]: !open }))}
                  >
                    <TableCell>
                      <IconButton size="small" aria-label={open ? 'Collapse tasks' : 'Expand tasks'}>
                        {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{participantLabel(row)}</TableCell>
                    <TableCell>{row.subject_type === 'guest' ? 'Guest' : 'User'}</TableCell>
                    <TableCell align="right">{formatNumber(row.day_number)}</TableCell>
                    <TableCell align="right">
                      {row.tasks_done}/{row.tasks_total}
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={4}>
                        {(row.tasks || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No tasks on this copy.
                          </Typography>
                        ) : (
                          (row.tasks || []).map((task) => (
                            <Typography key={`${key}-${task.task_key}`} variant="body2" sx={{ py: 0.25 }}>
                              {task.completed ? '[x]' : '[ ]'} {task.title || task.task_key}{' '}
                              <Typography component="span" variant="body2" color="text.secondary">
                                {task.completed ? 'Done' : 'Not done'}
                              </Typography>
                            </Typography>
                          ))
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {total === 0 ? '0 fans' : `${pageStart}–${pageEnd} of ${formatNumber(total)}`}
        </Typography>
        <Box>
          <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </Button>
          <Button size="small" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default DailyMissionAnalytics;
